import { Fragment, useCallback, useMemo, useState } from 'react';
import Table from 'shell/components/shared/table';
import FiltersRegionList from 'system/geographic/components/region/list/filters-region-list';
import { useEffect } from 'react';
import { FiltersFormRegion, RegionClient } from './types';
import { useFormik } from 'formik';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesRegion } from '..';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { generatePath, useNavigate } from 'react-router-dom';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonConsulateCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { Region } from 'shell/models/region';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import { transformDataset } from './utils';
import ListHeader from 'shell/components/shared/list-header';
import { authsObj, validationSchemaRegionFilter, headCells } from './constants';
import { Typography } from '@mui/material';

const RegionListPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<RegionClient>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<RegionClient>>({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<FiltersFormRegion>();

  const {
    data,
    get: getRegionsList,
    error,
  } = useHttpFetch<HttpResponseList<Region>>(`${__MUNICIPALITY_API_URL__}/geo/regione`, {
    data: {
      payload: {
        list: [],
      },
    },
  });

  const { delete: deleteRegion } = useHttpFetch<HttpResponse<Region>>(
    `${__MUNICIPALITY_API_URL__}/geo/regione`
  );

  useEffect(() => {
    const queryString = transformDataset(filterTable.values || initialValuesRegion);
    getRegionsList(queryString);
  }, []);

  const regionList: Array<RegionClient> = useMemo(() => {
    if (!!error || !data?.payload) {
      return [];
    }
    return data.payload.list.map((consulate) => ({
      code: consulate.key?.codice,
      name: consulate.nome,
    }));
  }, [data]);

  const count: number = useMemo(() => {
    if (!!error || !data?.payload) {
      return 0;
    }
    return data.payload.totalRows;
  }, [data]);

  const onSubmit = useCallback(
    (formValues: FiltersFormRegion) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterRegionForm.setFieldValue('page', 0);
      getRegionsList(queryString);
    },
    [getRegionsList]
  );

  const filterRegionForm = useFormik({
    initialValues: initialValuesRegion,
    validationSchema: validationSchemaRegionFilter,
    onSubmit,
  });
  useEffect(() => {
    filterRegionForm.setValues(filterTable.values || initialValuesRegion);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterRegionForm.values);
  }, [filterRegionForm.values]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterRegionForm.values, orders };
      const queryString = transformDataset(newDataset);
      return getRegionsList(queryString).then(() => {
        filterRegionForm.setFieldValue('orders', orders);
      });
    },
    [filterRegionForm.setFieldValue, getRegionsList, filterRegionForm.values]
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<RegionClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sistema/geografiche/italia/regione/dettaglio/:id', {
            id: cell.code,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<RegionClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sistema/geografiche/italia/regione/modifica/:id', {
            id: cell.code,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<RegionClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<RegionClient>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      filterRegionForm.setFieldValue('page', newPage);
    },
    [filterRegionForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...filterRegionForm.values, rowsPerPage: rowsPerPage || 0, page: 0 };
      filterRegionForm.setValues(newDataset);
    },
    [filterRegionForm.setFieldValue, filterRegionForm.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={CommonConsulateCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuova regione"
        addBtnLabel="Aggiungi nuova regione"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/geografiche/italia/regione/nuovo');
        }}
        title="Gestione Regioni"
      />
      <div className="pmi-my-3">
        <FiltersRegionList filterRegionForm={filterRegionForm} />
      </div>
      {data?.payload ? (
        regionList?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterRegionForm.values.orders}
            activeActions={auths}
            page={filterRegionForm.values.page}
            rowsPerPage={filterRegionForm.values.rowsPerPage}
            body={regionList}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Regioni"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione regione"
        message={`Confermare la cancellazione della regione ${
          deleteAlertModal.element?.name || ''
        }?`}
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          deleteRegion(`/${deleteAlertModal.element?.code}`).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione regione"
        message={`La cancellazione della regione ${
          deleteSuccessModal.element?.name || ''
        } è avvenuta con successo`}
        onClose={() => {
          const queryString = transformDataset(filterRegionForm.values);
          getRegionsList(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
        onOk={() => {
          const queryString = transformDataset(filterRegionForm.values);
          getRegionsList(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
      />
    </Fragment>
  );
};

RegionListPage.displayName = 'Regions List page component';

export default RegionListPage;
